/**
 * Terminal Authentication Utility
 * Handles API-key-based authentication for terminal kiosk mode
 */

// Determine API base URL based on environment
const getApiBaseUrl = (): string => {
    // 1. In production (Azure Static Web App - Production)
    if (window.location.hostname === 'jolly-water-0f9a5e900.2.azurestaticapps.net') {
        return 'https://intecai-production-h6ajbxe0gphqheds.germanywestcentral-01.azurewebsites.net';
    }

    // 2. In test/staging (Azure Static Web App - Test)
    if (window.location.hostname.includes('azurestaticapps.net')) {
        return 'https://intecai-cloud-test-fjfudkc5d5eecrc0.germanywestcentral-01.azurewebsites.net';
    }

    // 3. Localhost development - use local backend
    return 'http://localhost:4000';
};

const API_BASE_URL = getApiBaseUrl();
const TERMINAL_TOKEN_KEY = "ia_terminal_token";
const TERMINAL_MODE_KEY = "ia_terminal_mode";
const TERMINAL_INFO_KEY = "ia_terminal_info";

interface TerminalAuthResponse {
    success: boolean;
    token: string;
    terminalId: string;
    name: string;
    terminalNumber?: number;
}

interface TerminalInfo {
    terminalId: string;
    name: string;
    terminalNumber?: number;
    tenantId: string;
}

/**
 * Authenticates a terminal using an API key
 * @param apiKey - 8-character UPPERCASE API key
 * @param tenantIdentifier - Tenant ID or Tenant Slug
 * @returns Authentication result with token
 */
export async function authenticateTerminal(
    apiKey: string,
    tenantIdentifier: string
): Promise<TerminalAuthResponse> {
    // Determine if tenantIdentifier is an ID (24 hex chars) or slug
    const isTenantId = /^[0-9a-f]{24}$/i.test(tenantIdentifier);

    const response = await fetch(`${API_BASE_URL}/api/terminal/auth`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            apiKey: apiKey.toUpperCase(),
            ...(isTenantId ? { tenantId: tenantIdentifier } : { tenantSlug: tenantIdentifier }),
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Authentication failed" }));
        throw new Error(error.message || "Terminal authentication failed");
    }

    const data: TerminalAuthResponse = await response.json();

    if (data.success && data.token) {
        // Store token in localStorage (persistent across page reloads)
        localStorage.setItem(TERMINAL_TOKEN_KEY, data.token);
        localStorage.setItem(TERMINAL_MODE_KEY, "true");
        localStorage.setItem(
            TERMINAL_INFO_KEY,
            JSON.stringify({
                terminalId: data.terminalId,
                name: data.name,
                terminalNumber: data.terminalNumber,
                tenantId: tenantIdentifier,
            })
        );
    }

    return data;
}

/**
 * Checks if a terminal token exists in localStorage
 */
export function isTerminalAuthenticated(): boolean {
    return !!localStorage.getItem(TERMINAL_TOKEN_KEY);
}

/**
 * Gets the terminal authentication token from localStorage
 */
export function getTerminalToken(): string | null {
    return localStorage.getItem(TERMINAL_TOKEN_KEY);
}

/**
 * Gets stored terminal information from localStorage
 */
export function getTerminalInfo(): TerminalInfo | null {
    const infoStr = localStorage.getItem(TERMINAL_INFO_KEY);
    if (!infoStr) {
        return null;
    }

    try {
        return JSON.parse(infoStr);
    } catch {
        return null;
    }
}

/**
 * Checks if the app is running in terminal mode
 */
export function isTerminalMode(): boolean {
    return localStorage.getItem(TERMINAL_MODE_KEY) === "true";
}

/**
 * Clears terminal authentication (logout)
 */
export function clearTerminalAuth(): void {
    localStorage.removeItem(TERMINAL_TOKEN_KEY);
    localStorage.removeItem(TERMINAL_MODE_KEY);
    localStorage.removeItem(TERMINAL_INFO_KEY);
}

/**
 * Validates the current terminal token with the backend
 */
export async function validateTerminalToken(): Promise<boolean> {
    const token = getTerminalToken();
    if (!token) {
        return false;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/api/terminal/validate`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            return false;
        }

        const data = await response.json();
        return data.valid === true;
    } catch {
        return false;
    }
}
